/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { type ComponentType } from 'react';

import { jsx } from '@compiled/react';

import type { EnvironmentType } from '@atlassian/iap-types';
import type { UniversalCreateSourceProduct } from '@atlassian/universal-create-common';

import { useLoomVideoConfig } from './useLoomVideoConfig';
import { convertSourceProductToProductType } from './utils';

/**
 * Base configuration context interface - can be extended by specific contexts
 */
export interface LoomVideoConfigContext {
	cloudId: string;
	environment?: EnvironmentType;
	onItemClick?: (preset: string) => void;
}

/**
 * Props interface for components that can be wrapped with Loom video config
 */
export interface WithLoomVideoConfigProps {
	context: LoomVideoConfigContext;
	product: UniversalCreateSourceProduct;
	subProduct?: string;
}

/**
 * Higher-order component that wraps a component with Loom video configuration
 * @param WrappedComponent - The component to wrap with Loom video configuration
 * @returns A new component with Loom video configuration applied
 */
export function withLoomVideoConfig<P extends WithLoomVideoConfigProps>(
	WrappedComponent: ComponentType<P>,
): ComponentType<P> {
	return function WithLoomVideoConfigComponent(props: P) {
		const { context, product, subProduct } = props;

		const customisedItemConfig = useLoomVideoConfig({
			cloudId: context.cloudId,
			productName: convertSourceProductToProductType(product, subProduct),
			environment: context.environment || 'prod',
			onItemClick: context.onItemClick,
			product,
			subProduct,
		});

		return <WrappedComponent {...(props as P)} customisedItemConfig={customisedItemConfig} />;
	};
}
